import type { SequenceDocument, TrackType } from '../../format/types';
import { TRACK_TYPES } from '../../format/types';

interface StepGridProps {
  readonly doc: SequenceDocument;
  readonly currentStep: number;
  readonly isPlaying: boolean;
  readonly selectedEventId: string | undefined;
  readonly onCreateStep: (trackId: string, step: number) => void;
  readonly onSelectEvent: (eventId: string) => void;
  readonly onRenameTrack: (trackId: string, name: string) => void;
  readonly onSetTrackType: (trackId: string, type: TrackType) => void;
  readonly onSetTrackRole: (trackId: string, role: string) => void;
  readonly onSetTrackMute: (trackId: string, mute: boolean) => void;
  readonly onSetTrackSolo: (trackId: string, solo: boolean) => void;
  readonly onSetTrackLevel: (trackId: string, level: number) => void;
  readonly onRemoveTrack: (trackId: string) => void;
  readonly onClearTrack: (trackId: string) => void;
  readonly onAddTrack: () => void;
}

export function StepGrid(props: StepGridProps): JSX.Element {
  const { doc } = props;
  const steps = Array.from({ length: doc.timing.lengthSteps }, (_, index) => index);
  const beatBoundary = doc.timing.stepsPerBeat;

  return (
    <div className="step-editor">
      <div className="step-grid-scroll">
        <table className="step-grid" aria-label="Multitrack Step-Editor">
          <thead>
            <tr>
              <th className="track-header-cell">Spur</th>
              {steps.map((step) => (
                <th
                  key={step}
                  className={`step-header${step === props.currentStep && props.isPlaying ? ' current' : ''}${
                    step % beatBoundary === 0 ? ' beat-start' : ''
                  }`}
                >
                  {step + 1}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {doc.tracks.map((track) => {
              const eventsByStep = new Map(
                doc.events.filter((event) => event.trackId === track.id).map((event) => [event.step, event]),
              );
              return (
                <tr key={track.id} className={track.mute ? 'track-row muted' : 'track-row'}>
                  <td className="track-header-cell">
                    <div className="track-controls">
                      <input
                        className="track-name"
                        value={track.name}
                        onChange={(event) => props.onRenameTrack(track.id, event.target.value)}
                        aria-label="Spurname"
                      />
                      <select
                        value={track.type}
                        onChange={(event) => props.onSetTrackType(track.id, event.target.value as TrackType)}
                        aria-label="Tracktyp"
                      >
                        {TRACK_TYPES.map((type) => (
                          <option key={type} value={type}>
                            {type}
                          </option>
                        ))}
                      </select>
                      <input
                        className="track-role"
                        value={track.role}
                        onChange={(event) => props.onSetTrackRole(track.id, event.target.value)}
                        aria-label="Rolle"
                        placeholder="Rolle"
                      />
                      <div className="track-buttons">
                        <button
                          type="button"
                          className={track.mute ? 'toggle active' : 'toggle'}
                          onClick={() => props.onSetTrackMute(track.id, !track.mute)}
                          aria-pressed={track.mute}
                          title="Mute"
                        >
                          M
                        </button>
                        <button
                          type="button"
                          className={track.solo ? 'toggle active' : 'toggle'}
                          onClick={() => props.onSetTrackSolo(track.id, !track.solo)}
                          aria-pressed={track.solo}
                          title="Solo"
                        >
                          S
                        </button>
                        <input
                          type="range"
                          min={0}
                          max={1}
                          step={0.01}
                          value={track.level}
                          onChange={(event) => props.onSetTrackLevel(track.id, Number(event.target.value))}
                          aria-label="Pegel"
                          title="Pegel"
                        />
                      </div>
                      <div className="track-buttons">
                        <button type="button" onClick={() => props.onClearTrack(track.id)} title="Spur leeren">
                          Leeren
                        </button>
                        <button type="button" onClick={() => props.onRemoveTrack(track.id)} title="Spur loeschen">
                          Loeschen
                        </button>
                      </div>
                    </div>
                  </td>
                  {steps.map((step) => {
                    const event = eventsByStep.get(step);
                    const isCurrent = step === props.currentStep && props.isPlaying;
                    const isSelected = event !== undefined && event.id === props.selectedEventId;
                    return (
                      <td
                        key={step}
                        className={`step-cell${step % beatBoundary === 0 ? ' beat-start' : ''}${isCurrent ? ' current' : ''}`}
                      >
                        <button
                          type="button"
                          className={`step-button${event ? ' occupied' : ''}${isSelected ? ' selected' : ''}`}
                          onClick={() => {
                            if (event) {
                              props.onSelectEvent(event.id);
                            } else {
                              props.onCreateStep(track.id, step);
                            }
                          }}
                          aria-pressed={event !== undefined}
                          aria-label={`Spur ${track.name}, Step ${step + 1}${event ? ', belegt' : ', leer'}`}
                        />
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <button type="button" className="add-track" onClick={props.onAddTrack}>
        + Spur hinzufuegen
      </button>
    </div>
  );
}
